import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import logo from "../assets/images/White_Version.png";
import { useLocation } from "react-router-dom";
import { Shield,Star } from "lucide-react"
import { getAuthCookie } from "../utils/authCookies";

interface ModulePermission {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  status: boolean;
  volume: number;
}

interface Module {
  id: string;
  name: string;
  module_code: string;
  license_type_code: string;
  description: string;
  status: boolean;
  volume: number;
}

interface License {
  id: string;
  companyName: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  statuss: string;
  licenseValidity: string | null;
  billingCycle: string | null;
  billingCyclePrice: number | null;
  paymentMethod: string;
  licenseType: string;
  modules: Module[]; // Changed to Module[]
}

const sidebarItems = [
  {
    name: "Dashboard",
    path: "/realtor/dashboard",
    icon: "ri-dashboard-fill",
    module_code: "DASHBOARD",
  },
  {
    name: "Offices",
    path: "/realtor/offices",
    icon: "ri-building-2-line",
    module_code: "OFFICES",
  },
  {
    name: "Team Members",
    path: "/realtor/teammembers",
    icon: "ri-team-fill",
    module_code: "BROKERAGE_TEAM_MEMBERS",
  },
  {
    name: "Agents",
    path: "/realtor/agents",
    icon: "ri-user-3-line",
    module_code: "AGENTS",
  },
  // {
  //   name: "Groups",
  //   path: "/realtor/groups",
  //   icon: "ri-team-fill",
  //   module_code: "GROUPS",
  // },
  {
    name: "Vendors",
    path: "/realtor/suppliers",
    icon: "ri-store-2-line",
    module_code: "SUPPLIERS",
  },
  {
    name: "Inventory",
    path: "/realtor/inventory",
    icon: "ri-store-2-line",
    module_code: "INVENTORY",
  },
  {
    name: "Orders",
    path: "/realtor/orders",
    icon: "bx bx-cart-alt",
    module_code: "ORDERS",
  },
  {
    name: "Design Requests",
    path: "/realtor/design-requests",
    icon: "ri-folder-chart-line",
    module_code: "PROJECTS",
  },
  {
    name: "Jobs",
    path: "/realtor/jobs",
    icon: "ri-briefcase-fill",
    module_code: "JOBS",
  },
  {
    name: "Batch Payments",
    path: "/realtor/batchPayments",
    icon: "ri-file-text-line",
    module_code: "BATCH_PAYMENTS",
  },
  {
    name: "Reports",
    path: "/realtor/reports",
    icon: "ri-bar-chart-2-fill",
    module_code: "REPORTS",
  },
  // {
  //   name: "Addresses",
  //   path: "/realtor/addresses",
  //   icon: "ri-home-wifi-fill",
  //   module_code: "ADDRESSES",
  // },
  {
    name: "Roles",
    path: "/signage/roles",
    module_code: "ROLES",
  },
];

const RealtorSidebar = () => {
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const [license, setLicense] = useState<License | null>(null);
  const [modulePermissions, setModulePermissions] = useState<Module[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    const fetchLicense = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch license data");
        }

        const data = await response.json();
        console.log("Fetched License Data:", data);

        if (data && data.firmDetails && data.firmDetails.address) {
          const fetchedLicense: License = {
            id: data.id || "",
            companyName: data.firmDetails.companyName || "",
            jobTitle: data.jobTitle || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone1 || "",
            addressLine1: data.firmDetails.address.addressLine1 || "",
            addressLine2: data.firmDetails.address.addressLine2 || "",
            city: data.firmDetails.address.city || "",
            state: data.firmDetails.address.state || "",
            zip: data.firmDetails.address.zip || "",
            country: data.firmDetails.address.country || "",
            statuss: data.status || "Active",
            licenseValidity: data.licenseValidity || null,
            billingCycle: data.billingCycle || null,
            billingCyclePrice: data.billingCyclePrice || null,
            paymentMethod: data.paymentMethod || "",
            licenseType: data.licenseType || "",
            modules: Array.isArray(data.modulePermissions)
              ? data.modulePermissions
              : [],
          };

          setLicense(fetchedLicense);
          setModulePermissions(
            Array.isArray(data.modulePermissions) ? data.modulePermissions : []
          );
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching license data:", error);
        setError("Failed to fetch license data.");
      } finally {
        setLoading(false);
      }
    };

    if (!license) {
      fetchLicense();
    }
  }, [userId, license, token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.innerWidth < 992) {
      document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
    }
  }, [location.pathname]);

  return (
    <>
      {/* 🔐 Scoped CSS (in-file) */}
      <style>{`
        /* Scope to this component only */
        #realtor-sidebar .navbar-menu {
          overflow-y: auto;
          scrollbar-width: none;
        }

        /* MOBILE (≤767px): off-canvas + overlay */
        @media (max-width: 767px) {
          #realtor-sidebar .app-menu.navbar-menu {
            position: fixed;
            top: 0; left: 0; bottom: 0;
            width: 280px;
            z-index: 1040;
            transform: translateX(-100%);
            transition: transform .3s ease;
            background: #0b1324; /* your sidebar bg */
          }
          body.vertical-sidebar-enable #realtor-sidebar .app-menu.navbar-menu {
            transform: translateX(0);
          }

          /* Mobile header (logo + close) inside the sidebar */
          #realtor-sidebar .mobile-sidebar-header {
            position: sticky;
            top: 0;
            z-index: 3;
            background: #0b1324;
            border-bottom: 1px solid rgba(255,255,255,.08);
          }

          /* Hide desktop brand on mobile */
          #realtor-sidebar .navbar-brand-box {
            display: none !important;
          }

          /* Light overlay */
          #realtor-sidebar .sidebar-background {
            position: fixed;
            inset: 0;
            background: rgba(11,19,36,.3);
            opacity: 0;
            pointer-events: none;
            transition: opacity .2s ease;
            z-index: 1030; /* below sidebar */
          }
          body.vertical-sidebar-enable #realtor-sidebar .sidebar-background {
            opacity: .5;
            pointer-events: auto;
          }

          /* Lock page scroll while open (global) */
          body.vertical-sidebar-enable {
            overflow: hidden;
          }
        }

        /* TABLET & DESKTOP (≥768px): static sidebar; no overlay */
        @media (min-width: 768px) {
          #realtor-sidebar .app-menu.navbar-menu {
            position: static;
            transform: none !important;
            z-index: auto;
          }
          #realtor-sidebar .sidebar-background {
            display: none;
          }
        }
      `}</style>
      <div className="app-menu navbar-menu">
        {/* ✅ MOBILE top strip (logo + close), visible on ≤767px */}
        <div className="mobile-sidebar-header d-md-none d-flex align-items-center gap-2 px-3 py-2">
          <Link to="/realtor/welcome" className="d-inline-flex align-items-center text-decoration-none">
            <img src={logo} alt="Realtor" height={100} />
          </Link>
          <button
            type="button"
            aria-label="Close sidebar"
            className="btn btn-sm btn-icon btn-ghost-light ms-auto mt-3"
            onClick={() => {
              document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
            }}
          >
            <i className="ri-close-line fs-4"></i>
          </button>
        </div>

        <div className="navbar-brand-box d-none d-md-block">
          {/* Logo */}
          <Link to="/realtor/welcome" className="logo logo-dark">
            <span className="logo-sm">
              <img src={logo} alt="" height="22" />
            </span>
            <span className="logo-lg">
              <img src={logo} alt="" height="40" />
            </span>
          </Link>

          <Link to="/realtor/welcome" className="logo logo-light">
            <span className="logo-sm">
              <img src={logo} alt="" height="22" />
            </span>
            <span className="logo-lg">
              <img src={logo} alt="" height="90" style={{ marginTop: "10px" }} />
            </span>
          </Link>

          <button
            type="button"
            className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover"
            id="vertical-hover"
          >
            <i className="ri-record-circle-line"></i>
          </button>
        </div>

        {/* User Dropdown */}
        <div className="dropdown sidebar-user m-1 rounded">
          <button
            type="button"
            className="btn material-shadow-none"
            id="page-header-user-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <span className="d-flex align-items-center gap-2">
              <img
                className="rounded header-profile-user"
                src="assets/images/users/avatar-1.jpg"
                alt="Header Avatar"
              />
              <span className="text-start">
                <span className="d-block fw-medium sidebar-user-name-text">
                  Anna Adame
                </span>
                <span className="d-block fs-14 sidebar-user-name-sub-text">
                  <i className="ri-circle-fill fs-10 text-success align-baseline"></i>{" "}
                  <span className="align-middle">Online</span>
                </span>
              </span>
            </span>
          </button>

          <div className="dropdown-menu dropdown-menu-end">
            <h6 className="dropdown-header">Welcome Anna!</h6>
            <Link to="/realtor/profile" className="dropdown-item">
              <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
              <span className="align-middle">Profile</span>
            </Link>
            <LogoutButton />
          </div>
        </div>

        {/* Sidebar Navigation */}
        <div id="scrollbar">
          <div className="container-fluid">
            <ul className="navbar-nav" id="navbar-nav">
              {/* <li className="nav-item">
                <Link to="/realtor/welcome" className={`nav-link menu-link ${location.pathname === "/realtor/welcome" ? "active" : ""}`}>
                    <Star size={28} color="#ffffff" className="ms-1" />
                  <span data-key="t-dashboards">Welcome</span>
                </Link>
              </li> */}
              <li className="nav-item">
                <Link to="/realtor/dashboard" className={`nav-link menu-link ${location.pathname === "/realtor/dashboard" ? "active" : ""}`}>
                  <i className="ri-dashboard-2-line"></i> <span>Dashboard</span>
                </Link>
              </li>
              {sidebarItems?.map((item) => {
                const permission = modulePermissions?.find((perm) => perm.module_code === item.module_code);
                if (item.module_code === "INVENTORY") {
                  return (
                    <li className="nav-item">
                      <a
                        className="nav-link menu-link"
                        onClick={() => setOpenMenu(openMenu === 'inventoryMenu' ? null : 'inventoryMenu')}
                        href="#inventory-menu"
                        data-bs-toggle="collapse"
                        role="button"
                        aria-expanded="false"
                        aria-controls="sidebarLanding"
                      >
                        <i className="ri-rocket-line"></i>{" "}
                        <span data-key="t-landing">Inventory</span>
                      </a>
                      <div className={`collapse menu-dropdown ${openMenu === 'inventoryMenu' ? 'show' : ''}`} id="inventory-menu">
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link
                              to="/realtor/officeInventory"
                              className={`nav-link menu-link ${location.pathname === "/realtor/officeInventory" ? "active" : ""}`}
                              data-key="t-one-page"
                            >
                              {" "}
                              Office Inventory{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/realtor/agentInventory"
                              className={`nav-link menu-link ${location.pathname === "/realtor/agentInventory" ? "active" : ""}`}
                              data-key="t-one-page"
                            >
                              {" "}
                              Agent Inventory{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/realtor/categories"
                              className={`nav-link menu-link ${location.pathname === "/realtor/categories" ? "active" : ""}`}
                              data-key="t-nft-landing"
                            >
                              {" "}
                              Categories{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/realtor/brands"
                              className={`nav-link menu-link ${location.pathname === "/realtor/brands" ? "active" : ""}`}
                              data-key="t-job"
                            >
                              {" "}
                              Brands{" "}
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </li>
                  );
                }
                if (item.module_code === "JOBS") {
                  return (
                    <li className="nav-item" key={item.module_code}>
                      <a
                        className="nav-link menu-link"
                        onClick={() => setOpenMenu(openMenu === 'jobsMenu' ? null : 'jobsMenu')}
                        href="#jobs-menu"
                        data-bs-toggle="collapse"
                      >
                        <i className={item.icon}></i> <span>{item.name}</span>
                      </a>
                      <div className={`collapse menu-dropdown ${openMenu === 'jobsMenu' ? 'show' : ''}`} id="jobs-menu">
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link to="/realtor/jobs/installations"
                              className={`nav-link menu-link ${location.pathname === "/realtor/jobs/installations" ? "active" : ""}`}
                            >Installations</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/realtor/jobs/removalRequests"
                              className={`nav-link menu-link ${location.pathname === "/realtor/jobs/removalRequests" ? "active" : ""}`}
                            >Removal Requests</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/realtor/active-installations"
                              className={`nav-link menu-link ${location.pathname === "/realtor/active-installations" ? "active" : ""}`}
                            >Active Installations</Link>
                          </li>
                        </ul>
                      </div>
                    </li>
                  );
                }

                if (item.module_code === "REPORTS") {
                  return (
                    <li className="nav-item" key={item.module_code}>
                      <a
                        className="nav-link menu-link"
                        onClick={() => setOpenMenu(openMenu === 'reportsMenu' ? null : 'reportsMenu')}
                        href="#reports-menu"
                        data-bs-toggle="collapse"
                      >
                        <i className={item.icon}></i> <span>{item.name}</span>
                      </a>
                      <div className={`collapse menu-dropdown ${openMenu === 'reportsMenu' ? 'show' : ''}`} id="reports-menu">
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link to="/realtor/installationSummaryReport"
                              className={`nav-link menu-link ${location.pathname === "/realtor/installationSummaryReport" ? "active" : ""}`}
                            >Installation Summary Report</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/realtor/removalSummaryReport"
                              className={`nav-link menu-link ${location.pathname === "/realtor/removalSummaryReport" ? "active" : ""}`}
                            >Removal Summary Report</Link>
                          </li>
                          <li className="nav-item">
                            <Link to="/realtor/salesOrderReport"
                              className={`nav-link menu-link ${location.pathname === "/realtor/salesOrderReport" ? "active" : ""}`}
                            >SalesOrder Summary Report</Link>
                          </li>
                          {/* <li className="nav-item">
                            <Link to="/realtor/active-installations" 
                            className={`nav-link menu-link ${location.pathname === "/realtor/active-installations" ? "active" : ""}`}
                            >Active Installations</Link>
                          </li> */}
                        </ul>
                      </div>
                    </li>
                  );
                }

                if (item.module_code === "ROLES") {
                  return (
                    <li className="nav-item" key={item.module_code}>
                      <Link to="/realtor/roles"
                        className={`nav-link menu-link ${location.pathname === "/realtor/roles" ? "active" : ""}`}
                      >
                        <Shield className="ms-1 text-white" /> <span>{item.name}</span>
                      </Link>
                    </li>
                  )
                }

                return permission?.status ? (
                  <li className="nav-item" key={item.module_code}>
                    <Link to={item.path}
                      className={`nav-link menu-link ${location.pathname === item.path ? "active" : ""}`}
                    >
                      <i className={item.icon}></i> <span>{item.name}</span>
                    </Link>
                  </li>
                ) : null;
              })}

              <li className="nav-item">
                <Link to="/realtor/profile"
                  className={`nav-link menu-link ${location.pathname === "/realtor/profile" ? "active" : ""}`}
                >
                  <i className="ri-user-line"></i>{" "}
                  <span data-key="t-dashboards">Profile</span>
                </Link>
              </li>
              <li className="nav-item">
                <LogoutButton />
              </li>
            </ul>
          </div>
        </div>

        <div
          className="sidebar-background"
          onClick={() => {
            document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
          }}
        ></div>
      </div>
      {/* ✅ Overlay OUTSIDE the sidebar. Clicking it closes the sidebar */}
      <div
        className="sidebar-background"
        onClick={() => {
          document.body.classList.remove("vertical-sidebar-enable", "sidebar-enable");
        }}
      />
    </>
  );
};

export default RealtorSidebar;
